/* eslint-disable max-classes-per-file */
import store from 'store/store';
import {
  DocumentTypes,
  DocumentType,
  Difference,
  DifferenceTypeNames,
  DiffToAnnotData,
  ZoneCustomData,
  AnnotationCustomData,
  GraphicZoneCustomData,
  MarqueeZoneCustomData,
  PDFTronTools,
  InputAnnotation,
  Quad,
  CropZoneCustomData,
  PolygonZoneCustomData,
  ContentDifferences,
  FormattingDifferences,
  RepeatAnnotationCustomData,
  SpellingDifferences,
  ZoomLevelLimits,
  AnnotationVisibilityOptions,
} from 'types';
import {
  inspection,
  getTotalPageCount,
  getGroupOrDifferenceId,
  getGroupedDifferences,
  getFocusedDifference,
  getFocusedDifferenceId,
  getHideOtherDifferences,
  getDifferences,
  getDisplayedAndHiddenDifferences,
  getIsSingleFile,
  app,
} from 'store';
import Utils from 'components/PDFViewer/Utils';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import DifferenceClass, { normalizeAnnotationRect } from 'pdftron/docManager/Difference';
import { planDifferenceBoxVisibility } from 'pdftron/docManager/shouldHideUnselectedDifferenceBox';
import { hasLoadedDocument } from 'pdftron/utils/WebViewerUtils';
import quadsToDimensions from 'pdftron/docManager/utils/quadsToDimensions';
import GVAnnotationMixin from 'pdftron/docManager/AnnotationTools/utils/GVAnnotationMixin';
import { CtfMatchResultWithDifferences } from 'store/request/match/types';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import {
  getPageRotation,
  calculateRotatedLabelPosition,
  drawCounterRotatedLabel,
  calculateLabelBounds,
} from 'pdftron/docManager/AnnotationTools/utils/labelPositionUtils';

export default class PDFAnnotationManager {
  private static AnnotationManagerInstances: { [key in DocumentTypes]: Core.AnnotationManager | null } | undefined;

  // keeps track of the selected annotation id
  static selectedId: string;

  /**
   * Sets the instances of AnnotationManager
   * @param type The related document
   * @param annotationManager The instance of annotation manager
   */
  static setInstance(type: DocumentType, annotationManager: Core.AnnotationManager): void {
    if (!PDFAnnotationManager.AnnotationManagerInstances) {
      PDFAnnotationManager.AnnotationManagerInstances = {
        source: null,
        target: null,
      };
    }

    PDFAnnotationManager.AnnotationManagerInstances[type] = annotationManager;
  }

  /**
   * Gets the AnnotationManager instance for a document
   * @param type The document which AnnotationManager is attached to
   */
  static getInstance(type: DocumentType): Core.AnnotationManager | null {
    if (!PDFAnnotationManager.AnnotationManagerInstances) return null;
    if (!PDFAnnotationManager.AnnotationManagerInstances[type]) return null;
    return PDFAnnotationManager.AnnotationManagerInstances[type];
  }

  static clearInstance(type: DocumentType): void {
    if (!PDFAnnotationManager.AnnotationManagerInstances) return;
    PDFAnnotationManager.AnnotationManagerInstances[type] = null;
  }

  /**
   * Gets the current annotations from a document.
   * Note that these are not the raw annotations from PDFTron.
   * @param type The related document
   */
  static getInputAnnotations(type: DocumentType): InputAnnotation[] | null {
    const annotationManager = PDFAnnotationManager.getInstance(type);
    if (!annotationManager) {
      return null;
    }
    // Filter internal annotations of PDFTron
    const filteredAnnotations = annotationManager
      .getAnnotationsList()
      .filter(
        (annotation) =>
          annotation.getCustomData(AnnotationCustomData.strikeoutAnnotation) === 'true' ||
          annotation.getCustomData(ZoneCustomData.zoneAnnotation) === 'true' ||
          annotation.getCustomData(GraphicZoneCustomData.graphicAnnotation) === 'true' ||
          annotation.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true' ||
          annotation.getCustomData(CropZoneCustomData.cropZoneAnnotation) === 'true' ||
          annotation.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true',
      );
    return PDFManagerFactory.getPDFDocManager()?.convertToInputAnnotation(filteredAnnotations) || null;
  }

  /**
   * Delete all input annotations by document type
   * @param documentType The related document
   */
  static deleteInputAnnotationsByDocument(documentType: DocumentType): void {
    const annotationManager = PDFAnnotationManager.getInstance(documentType);
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (!annotationManager || !pdfDocManager) return;

    // Gather all annotations and filter them as input annotations using the shared filter
    const filter = pdfDocManager.getInputAnnotationFilter();
    const annotationsToDelete = annotationManager.getAnnotationsList().filter(filter);

    annotationManager.deleteAnnotations(annotationsToDelete, { imported: true, force: true });
  }

  /**
   * Delete annotations by id
   * @param documentType The related document
   * @param id The id of annotation
   */
  static deleteAnnotationById(documentType: DocumentType, id: string): void {
    const inputAnnotations = PDFAnnotationManager.getInputAnnotations(documentType);
    const annotationManager = PDFAnnotationManager.getInstance(documentType);
    if (!inputAnnotations || !annotationManager) {
      return;
    }
    const inputAnnotationsToDelete = inputAnnotations.filter(
      (inputAnnot) =>
        inputAnnot.annotationId.split('_')[0] === id && annotationManager.getAnnotationById(inputAnnot.annotationId),
    ); // split function is for the highlight annotations on target file
    const annotationsToDelete: Core.Annotations.Annotation[] = inputAnnotationsToDelete.map((inputAnnotation) =>
      annotationManager.getAnnotationById(inputAnnotation.annotationId),
    );

    annotationManager.deleteAnnotations(annotationsToDelete, {
      imported: true,
      force: true,
    });
  }

  /**
   * Delete highlight annotations by document type
   * @param documentType The related document
   * @param id The id of annotation
   */
  static deleteHighlightAnnotations(documentType: DocumentType, id: string): void {
    const inputAnnotations = PDFAnnotationManager.getInputAnnotations(documentType);
    const annotationManager = PDFAnnotationManager.getInstance(documentType);
    if (!inputAnnotations || !annotationManager) {
      return;
    }
    const highlightAnnotations = inputAnnotations
      .filter((inputAnnot) => inputAnnot.annotationId === id)
      .map((inputAnnot) => annotationManager.getAnnotationById(inputAnnot.annotationId));

    annotationManager.deleteAnnotations(highlightAnnotations, {
      imported: true,
      force: true,
    });
  }

  /**
   * Delete all input annotations in both documents
   */
  static deleteAllInputAnnotations(): void {
    PDFAnnotationManager.deleteInputAnnotationsByDocument(DocumentTypes.source);
    PDFAnnotationManager.deleteInputAnnotationsByDocument(DocumentTypes.target);
  }

  static highlightDocumentDifference = (documentType: DocumentType, diffId: string) => {
    const annotManager = PDFAnnotationManager.getInstance(documentType);

    if (!annotManager) return;

    const textAnnot = annotManager.getAnnotationById(diffId);
    if (textAnnot) {
      Utils.styleSelectedRectangleAnnotation(textAnnot as Core.Annotations.RectangleAnnotation);
      annotManager.redrawAnnotation(textAnnot);
      annotManager.jumpToAnnotation(textAnnot);
    }
  };

  static highlightDifference(differenceId: string): void {
    this.highlightDocumentDifference(DocumentTypes.source, differenceId);
    this.highlightDocumentDifference(DocumentTypes.target, differenceId);
  }

  /**
   * This function draws zones on both source and target documents, so it needs
   * both of them to be available and loaded
   *
   * @param state Store state
   * @param dispatch Redux dispatch function
   * @pdftron-refactor This is now being used in the saga - it can be refactored, split in parts, put into mixins etc.
   */
  public static drawZones(): void {
    const { dispatch } = store;
    const sourceInstance = PDFTronManager.getPDFInstance(DocumentTypes.source);
    const targetInstance = PDFTronManager.getPDFInstance(DocumentTypes.target);
    if (!sourceInstance || !targetInstance) return;
    const sourceAnnotManager = sourceInstance.Core.documentViewer.getAnnotationManager();
    const targetAnnotManager = targetInstance.Core.documentViewer.getAnnotationManager();
    if (!sourceAnnotManager || !targetAnnotManager) return;

    // Get both documents
    const sourceDocument = sourceInstance.Core.documentViewer.getDocument();
    const targetDocument = targetInstance.Core.documentViewer.getDocument();

    // Helper for creating zone annotations and searching their associated text
    const createAnnotationPromise = async (
      zoneId: number,
      annotationMarkup: InputAnnotation,
      annotationManager: Core.AnnotationManager,
      instance: WebViewerInstance,
      document: Core.Document,
      editable: boolean,
    ): Promise<string> => {
      const manager = PDFManagerFactory.getPDFDocManager();
      if (!manager) return Promise.reject(new Error('invalid manager instance'));
      const annotation = new (manager.createZoneAnnotation(instance))(zoneId, editable); // Specify zoneId
      annotation.setCustomData(ZoneCustomData.zoneAnnotation, 'true');
      annotation.Id = annotationMarkup.annotationId;
      annotation.PageNumber = annotationMarkup.page;
      // Restore group id if present
      if (annotationMarkup.groupId) {
        annotation.setCustomData(ZoneCustomData.zoneGroup, annotationMarkup.groupId);
      }
      if (annotationMarkup.quads) {
        (annotationMarkup.quads as Quad[]).forEach((quad) => {
          annotation.Quads.push(
            new instance.Core.Math.Quad(quad.x1, quad.y1, quad.x2, quad.y2, quad.x3, quad.y3, quad.x4, quad.y4),
          );
        });
        const dimensionFromQuads = quadsToDimensions(annotation.Quads);
        annotation.setRect(
          new instance.Core.Math.Rect(
            dimensionFromQuads.X,
            dimensionFromQuads.Y,
            dimensionFromQuads.X + dimensionFromQuads.Width,
            dimensionFromQuads.Y + dimensionFromQuads.Height,
          ),
        );
      }

      annotation.ReadOnly = editable;
      annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
      annotation.setCustomData(ZoneCustomData.confirmed, 'true');
      annotationManager.addAnnotation(annotation);
      // force redraw to avoid weird behaviors caused by loading time
      annotationManager.redrawAnnotation(annotation);
      const rect = annotation.getRect();

      return PDFTronManager.extractText(document, annotationMarkup.page, rect.x1, rect.y1, rect.x2, rect.y2);
    };

    // Loop through zone annotations in store for source document
    const state = store.getState();
    const sourceZoneAnnotations = state.inspection.source.annotations.filter(
      (annot: InputAnnotation) => annot.usedTool === PDFTronTools.ZONE,
    );
    const targetZoneAnnotations = state.inspection.target.annotations.filter(
      (annot: InputAnnotation) => annot.usedTool === PDFTronTools.ZONE,
    );

    sourceZoneAnnotations
      .filter((annot: InputAnnotation) => annot.usedTool === PDFTronTools.ZONE) // Only zones
      .forEach(async (markup: InputAnnotation, index: number) => {
        // We need to check if the annotation is already there so we won't be adding it again
        const existingAnnotation = sourceAnnotManager.getAnnotationById(markup.annotationId);
        if (existingAnnotation) return;

        if (!markup.number) return;
        const zoneId = markup.number;

        // Here we need to create the two annotations and get their associated texts to complete the restore
        const sourceContent = await createAnnotationPromise(
          zoneId,
          markup,
          sourceAnnotManager,
          sourceInstance,
          sourceDocument,
          false,
        );

        if (!state.inspection.source.zoneSelectedTexts[index]) {
          // We now have both text samples, dispatch the needed Redux actions.
          // Set the selected text on source
          dispatch(
            inspection.actions.addZoneSelectedTexts({
              documentType: DocumentTypes.source,
              selectedText: sourceContent,
              zoneId: `${zoneId}`,
              annotationId: markup.annotationId,
            }),
          );
        }
      });

    // Loop through zone annotations in store for target document
    targetZoneAnnotations
      .filter((annot: InputAnnotation) => annot.usedTool === PDFTronTools.ZONE) // Only zones
      .forEach(async (markup: InputAnnotation, index: number) => {
        if (!markup.number) return;
        const zoneId = markup.number;

        const targetContent = await createAnnotationPromise(
          zoneId,
          markup,
          targetAnnotManager,
          targetInstance,
          targetDocument,
          true,
        );

        const targetAnnotation = {
          annotationId: markup.annotationId,
          text: targetContent,
          zoneId: Number(zoneId),
        };
        dispatch(
          inspection.actions.addZoneSearchedAnnotation({
            searchedAnnotation: targetAnnotation,
          }),
        );
        // And finally set the link to the zone on the target document
        dispatch(inspection.actions.setDrawZoneSelected({ zoneId }));
      });

    if (sourceZoneAnnotations.length) {
      const maxZoneId = Math.max(...sourceZoneAnnotations.map((annot: InputAnnotation) => annot.number || 0));
      dispatch(inspection.actions.setTextZoneId(maxZoneId + 1));
    } else {
      dispatch(inspection.actions.setTextZoneId(1));
    }
  }

  public static drawAllTexts(differences: Array<Difference>, document: DocumentType): void {
    const instance = PDFTronManager.getPDFInstance(document);
    if (!hasLoadedDocument(instance)) return;

    const instanceAnnotationManager = instance.Core.documentViewer.getAnnotationManager();
    if (!instanceAnnotationManager) return;

    const diffToAnnot = new Map<string, Core.Annotations.Annotation | null>();

    const validDifferences = differences.filter((diff) => diff[document].location.pageNumber !== 0);

    // Add visible difference annotations to the lookup map.
    differences.forEach((diff) => {
      if (diff[document].location.pageNumber === 0) {
        return;
      }
      diffToAnnot.set(diff.id, null);
    });

    DifferenceClass.drawTextHighlights(validDifferences, instance, document);
    PDFAnnotationManager.drawDifferences(instanceAnnotationManager, document, diffToAnnot, validDifferences);
    PDFAnnotationManager.syncHiddenOtherDifferenceBoxes();
  }

  /**
   * Lightweight update for a single difference's annotation on one document side.
   * Updates position, visibility, and page without iterating all differences.
   */
  public static updateSingleDifference(diff: Difference, documentType: DocumentTypes): void {
    const instance = PDFTronManager.getPDFInstance(documentType);
    if (!instance) return;
    const annotManager = instance.Core.documentViewer.getAnnotationManager();
    const annot = annotManager.getAnnotationById(diff.id);
    if (!annot) return;

    const loc = diff[documentType]?.location;
    if (!loc?.rect) return;

    const rect = loc.rect;
    const growth = Utils.annotationOptions.unselectedRectangeGrowthValue;
    const norm = normalizeAnnotationRect(rect, growth);
    annot.setX(norm.x);
    annot.setY(norm.y);
    annot.setWidth(norm.w);
    annot.setHeight(norm.h);

    const visibility = DifferenceClass.differenceAnnotationVisibility(
      diff.type,
      documentType,
      diff.embeddedImageDetails,
      rect,
    );
    annot.setCustomData(AnnotationCustomData.hiddenByHideOtherDifferences, 'false');
    if (visibility === AnnotationVisibilityOptions.visible) {
      annot.Hidden = false;
      annot.NoView = false;
    } else if (visibility === AnnotationVisibilityOptions.placeholder) {
      annot.Hidden = true;
      annot.NoView = false;
    } else {
      annot.NoView = true;
    }

    const totalPages = getTotalPageCount(store.getState())[documentType];
    const newPageNumber = Math.min(Math.max(1, loc.pageNumber), totalPages);
    if (newPageNumber !== annot.PageNumber) {
      annotManager.deleteAnnotation(annot, { imported: true });
      annot.setPageNumber(newPageNumber);
      annotManager.addAnnotation(annot, { imported: true });
    }
    annotManager.redrawAnnotation(annot);
  }

  /**
   * Helper to identify if an annotation is a difference or text highlight annotation
   */
  private static isDifferenceOrTextHighlightAnnotation(annot: Core.Annotations.Annotation): {
    isDifferenceAnnotation: boolean;
    isTextHighlightAnnotation: boolean;
  } {
    const isDifferenceAnnotation = annot.getCustomData('difference') === 'true';
    const isTextHighlightAnnotation =
      !!annot.Id && (annot.Id.endsWith('-text-highlight') || annot.getCustomData('isTextHighlight') === 'true');

    return { isDifferenceAnnotation, isTextHighlightAnnotation };
  }

  private static drawDifferences(
    annotManager: Core.AnnotationManager,
    documentType: DocumentTypes,
    diffToAnnot: Map<string, Core.Annotations.Annotation | null>,
    differences: Difference[],
  ) {
    const instance = PDFTronManager.getPDFInstance(documentType);
    if (!hasLoadedDocument(instance)) return;

    const DifferenceAnnotation = DifferenceClass.createDifferenceAnnotation(instance, documentType);

    const toDelete: Core.Annotations.Annotation[] = [];
    const toAdd: Core.Annotations.Annotation[] = [];
    const currentAnnotations = annotManager.getAnnotationsList();
    const totalPages = getTotalPageCount(store.getState())[documentType];
    // This exists to be used by redrawAnnotation. redrawAnnotation redraws all annoations on the same page so we only need to pass it one annotation on each page.
    const firstAnnotationByPage: (Core.Annotations.Annotation | null)[] = [...Array(totalPages)].map((index) => null);

    const state = store.getState();
    // Use getDisplayedAndHiddenDifferences to respect both type filters and module filters
    const { displayed, hidden } = getDisplayedAndHiddenDifferences(state);
    const hiddenSet = new Set(hidden);
    const displayedSet = new Set(displayed);
    const allDifferencesList = getDifferences(state);
    const allDifferenceIds = new Set(allDifferencesList.map((d) => d.id));

    // Find and mark orphaned annotations for deletion (annotations without corresponding differences after resync)
    // Only clean up orphaned annotations that truly no longer exist in Redux state
    // Use the full differences list from the store instead of the currently displayed subset
    if (allDifferencesList.length === 0) {
      // When there are no differences remaining (e.g., after resync removed all), delete all difference annotations
      currentAnnotations.forEach((annot) => {
        const { isDifferenceAnnotation, isTextHighlightAnnotation } =
          PDFAnnotationManager.isDifferenceOrTextHighlightAnnotation(annot);

        if (isDifferenceAnnotation || isTextHighlightAnnotation) {
          toDelete.push(annot);
        }
      });
    } else if (allDifferenceIds.size > 0) {
      // When we have differences, clean up orphaned ones AND hidden ones
      currentAnnotations.forEach((annot) => {
        const { isDifferenceAnnotation, isTextHighlightAnnotation } =
          PDFAnnotationManager.isDifferenceOrTextHighlightAnnotation(annot);

        if (isDifferenceAnnotation) {
          const diffId = annot.Id;
          if (!allDifferenceIds.has(diffId)) {
            // This annotation exists but its difference was removed (orphaned)
            toDelete.push(annot);
            // Also remove any paired text highlight annotation
            const textHighlight = annotManager.getAnnotationById(`${diffId}-text-highlight`);
            if (textHighlight) {
              toDelete.push(textHighlight);
            }
          } else {
            // Check if this difference is hidden by filters (type or module)
            const diff = allDifferencesList.find((d) => d.id === diffId);
            if (diff && hiddenSet.has(diff)) {
              // This annotation exists but its difference is hidden by filters (e.g., Graphics when Text module is selected)
              toDelete.push(annot);
              // Also remove any paired text highlight annotation
              const textHighlight = annotManager.getAnnotationById(`${diffId}-text-highlight`);
              if (textHighlight) {
                toDelete.push(textHighlight);
              }
            }
          }
        } else if (isTextHighlightAnnotation) {
          const parentId = annot.Id.replace('-text-highlight', '');
          if (!allDifferenceIds.has(parentId)) {
            // This text highlight's parent difference was removed (orphaned)
            toDelete.push(annot);
          } else {
            // Check if parent difference is hidden by filters
            const parentDiff = allDifferencesList.find((d) => d.id === parentId);
            if (parentDiff && hiddenSet.has(parentDiff)) {
              // This text highlight's parent difference is hidden by filters
              toDelete.push(annot);
            }
          }
        }
      });
    }

    // set any existing annotations in the map of differences
    // The draw-match flow can create a second annotation reusing an existing
    // difference ID (MarkupZone sets this.Id = differenceId).  Track seen IDs
    // so that only one annotation survives per difference; extras are deleted.
    const seenDiffAnnotIds = new Set<string>();
    currentAnnotations.forEach((annot) => {
      if (diffToAnnot.has(annot.Id)) {
        if (seenDiffAnnotIds.has(annot.Id)) {
          toDelete.push(annot);
        } else {
          seenDiffAnnotIds.add(annot.Id);
          diffToAnnot.set(annot.Id, annot);
        }
      }
    });

    differences.forEach((diff) => {
      // Skip differences with pageNumber 0 to prevent annotation merged graphics)
      if (diff.excluded) {
        // Also delete any existing excluded annotations from the manager
        const existingAnnotation = diffToAnnot.get(diff.id);
        if (existingAnnotation) {
          toDelete.push(existingAnnotation);
        }
        // Check for existing text highlight annotations
        const existingTextHighlight = annotManager.getAnnotationById(`${diff.id}-text-highlight`);
        if (existingTextHighlight) {
          toDelete.push(existingTextHighlight);
        }
        return;
      }

      const annotation = diffToAnnot.get(diff.id);
      const page = diff[documentType].location.pageNumber - 1;
      if (annotation) {
        if (hiddenSet.has(diff)) {
          // annotation exists and should be deleted
          toDelete.push(annotation);
          if (!firstAnnotationByPage[page]) {
            firstAnnotationByPage[page] = annotation;
          }
        }
        // Handle deletion markers for embedded images on the appropriate document
        const embeddedImageDeletionTarget = DifferenceClass.getEmbeddedImageDeletionTarget(diff);
        const shouldShowDeletionMarker =
          (DifferenceClass.requiresDeletionMarker([diff]) &&
            DocumentTypes.target === documentType &&
            !embeddedImageDeletionTarget) ||
          embeddedImageDeletionTarget === documentType;

        if (shouldShowDeletionMarker) {
          // If you delete it and draw a new one, it de-selects it. This is a workaround deselecting.

          // For embedded images, use coordinates from the document where the marker IS (not where the image is)
          let targetLocation;
          if (embeddedImageDeletionTarget === 'source') {
            // Marker is on source document (image exists in target, missing in source)
            // Prefer matchDetails.masterLocation for backward compatibility (old markers saved moved position there)
            const matchDetails = diff.embeddedImageDetails?.matchDetails?.[0];
            if (matchDetails?.masterLocation?.boundingBox && matchDetails.masterLocation.pageNumber > 0) {
              // Convert boundingBox to rect format [x, y, width, height]
              const boundingBox = matchDetails.masterLocation.boundingBox;
              const x = boundingBox.left;
              const y = boundingBox.top;
              const width = boundingBox.right - boundingBox.left;
              const height = boundingBox.bottom - boundingBox.top;
              targetLocation = {
                rect: [x, y, width, height],
                pageNumber: matchDetails.masterLocation.pageNumber,
              };
            } else {
              // Fall back to target.location
              targetLocation = diff.target.location;
            }
          } else if (embeddedImageDeletionTarget === 'target') {
            // Marker is on target document
            // Prefer matchDetails.sampleLocation for backward compatibility (old markers saved moved position there)
            const matchDetails = diff.embeddedImageDetails?.matchDetails?.[0];
            if (matchDetails?.sampleLocation?.boundingBox && matchDetails.sampleLocation.pageNumber > 0) {
              // Convert boundingBox to rect format [x, y, width, height]
              const boundingBox = matchDetails.sampleLocation.boundingBox;
              const x = boundingBox.left;
              const y = boundingBox.top;
              const width = boundingBox.right - boundingBox.left;
              const height = boundingBox.bottom - boundingBox.top;
              targetLocation = {
                rect: [x, y, width, height],
                pageNumber: matchDetails.sampleLocation.pageNumber,
              };
            } else {
              // Fall back to source.location
              targetLocation = diff.source.location;
            }
          } else {
            // Regular deletion marker logic
            targetLocation = diff.target.location;
          }

          annotation.setX(targetLocation.rect[0]);
          annotation.setY(targetLocation.rect[1]);

          if (targetLocation.pageNumber !== annotation.PageNumber) {
            // occurs when deletion marker has been moved to new page so we need to redraw it
            const totalPages = getTotalPageCount(store.getState())[documentType];
            const newPageNumber = targetLocation.pageNumber <= totalPages ? targetLocation.pageNumber : totalPages;
            annotation.setPageNumber(newPageNumber);
            const docManager = PDFManagerFactory.getPDFDocManager();
            docManager?.applyHighContrast([diff]);
          }
        } else if (
          !hiddenSet.has(diff) &&
          diff.type === DifferenceTypeNames.EmbeddedImage &&
          diff.embeddedImageDetails?.userLinkedMatch
        ) {
          // User-linked embedded image: sync annotation bounds/page from Redux so the
          // focus pipeline (applyHighContrast, zoomToDifference, scrollToDifferences)
          // targets the correct location instead of the stale placeholder at (0,0).
          const loc = diff[documentType].location;
          const rect = loc.rect;
          const growth = Utils.annotationOptions.unselectedRectangeGrowthValue;
          const norm = normalizeAnnotationRect(rect, growth);
          annotation.setX(norm.x);
          annotation.setY(norm.y);
          annotation.setWidth(norm.w);
          annotation.setHeight(norm.h);

          const visibility = DifferenceClass.differenceAnnotationVisibility(
            diff.type,
            documentType,
            diff.embeddedImageDetails,
            rect,
          );
          if (visibility === AnnotationVisibilityOptions.visible) {
            annotation.Hidden = false;
            annotation.NoView = false;
          } else if (visibility === AnnotationVisibilityOptions.placeholder) {
            annotation.Hidden = true;
            annotation.NoView = false;
          } else {
            annotation.NoView = true;
          }

          const totalPages = getTotalPageCount(store.getState())[documentType];
          const newPageNumber = Math.min(Math.max(1, loc.pageNumber), totalPages);
          if (newPageNumber !== annotation.PageNumber) {
            // PDFTron doesn't reliably re-index a hidden placeholder on a new page via
            // setPageNumber alone. Remove + re-add the same object so the annotation
            // manager registers it on the correct page.
            annotManager.deleteAnnotation(annotation, { imported: true });
            annotation.setPageNumber(newPageNumber);
            annotManager.addAnnotation(annotation, { imported: true });
          }
          annotManager.redrawAnnotation(annotation);
        }
      } else if (displayedSet.has(diff)) {
        // annotation does not exist and should be created (displayed and not filtered by type or module)

        // Text Highlights (VE-10556) we need to add highlights to text differences.
        // We need this to show the highlight when we click on the text difference.
        // 1. check if difference is a text difference. if it is, we need to add textHighlights to the annotation.
        // 2. if textFragments IS present, we highlight using coordinates of textFragments.
        // 3. if textFragments IS NOT present, we highlight using bounding box coordinates instead.
        let textHighlights = undefined;
        if (
          (diff.type in ContentDifferences || diff.type in FormattingDifferences || diff.type in SpellingDifferences) &&
          diff.type !== DifferenceTypeNames.EmbeddedImage
        ) {
          if (diff[documentType].textFragments) {
            textHighlights = diff[documentType].textFragments;
          } else {
            // populate textHighlights with bounding box coordinates
            textHighlights = [
              {
                pageNumber: diff[documentType].location.pageNumber,
                rect: diff[documentType].location.rect,
              },
            ];
          }
        }
        // Create missing content marker for:
        // - Deletion differences on target document (content missing from target)
        // - Insertion differences on source document (content missing from source)
        const needsMarker =
          (diff.type === DifferenceTypeNames.Deletion && documentType === DocumentTypes.target) ||
          (diff.type === DifferenceTypeNames.Insertion && documentType === DocumentTypes.source);

        if (needsMarker) {
          const MarkerAnnotClass = DifferenceClass.createMissingContentMarkerAnnotation(instance);
          // Use source location for insertions, target location for deletions
          const location = diff.type === DifferenceTypeNames.Insertion ? diff.source.location : diff.target.location;
          const newAnnotation = new MarkerAnnotClass({
            differenceId: diff.id,
            position: location.rect,
            pageNumber: location.pageNumber,
            groupId: diff.groupId,
            differenceType: diff.type,
          });
          toAdd.push(newAnnotation);
          if (!firstAnnotationByPage[page]) {
            firstAnnotationByPage[page] = newAnnotation;
          }
        } else {
          // Create regular difference annotation for all other cases
          const annotData: DiffToAnnotData = {
            ...diff[documentType],
            transformCoord: diff.transformCoord || false,
          };

          const newAnnotation = new DifferenceAnnotation({
            annotData,
            differenceId: diff.id,
            type: diff.type,
            groupId: diff.groupId,
            textHighlights,
            embeddedImageDetails:
              diff.type === DifferenceTypeNames.EmbeddedImage ? diff.embeddedImageDetails : undefined,
            barcodeDetails: diff.type === DifferenceTypeNames.Barcode ? diff.barcodeDetails : undefined,
            matchIndex: diff.matchIndex,
          });
          toAdd.push(newAnnotation);
          if (!firstAnnotationByPage[page]) {
            firstAnnotationByPage[page] = newAnnotation;
          }
        }
      }
    });
    if (toDelete.length) {
      annotManager.deleteAnnotations(toDelete, { imported: true });
    }
    if (toAdd.length) {
      annotManager.addAnnotations(toAdd);
    }

    firstAnnotationByPage.forEach((annot) => {
      if (annot) {
        annotManager.redrawAnnotation(annot);
      }
    });
    PDFAnnotationManager.bringInternalAnnotationsToFront(documentType);

    // Bring difference annotations back to front so their borders are visible on top of text highlights
    const allDifferenceAnnotations = annotManager
      .getAnnotationsList()
      .filter((annot) => annot.getCustomData('difference') === 'true');
    if (allDifferenceAnnotations.length > 0) {
      PDFAnnotationManager.bringAnnotationsToFront(documentType, allDifferenceAnnotations);
    }
  }

  public static deleteRectangleAnnotation(differenceId: string): void {
    const deleteDocumentAnnotation = (documentType: DocumentType, diffId: string) => {
      const annotManager = PDFAnnotationManager.getInstance(documentType);
      if (!annotManager) return;

      const annot = annotManager.getAnnotationById(diffId);
      if (annot) {
        annotManager.deleteAnnotation(annot, { imported: true });
      }
    };

    deleteDocumentAnnotation(DocumentTypes.source, differenceId);
    deleteDocumentAnnotation(DocumentTypes.target, differenceId);
  }

  public static redrawDifferenceAnnotations(): void {
    [DocumentTypes.source, DocumentTypes.target].forEach((document) => {
      const annotManager = PDFAnnotationManager.getInstance(document);
      if (!annotManager) return;
      annotManager.getAnnotationsList().forEach((annot) => {
        if (annot.getCustomData('difference') === 'true') {
          annotManager.redrawAnnotation(annot);
        }
      });
    });
  }

  public static redrawDifferenceBoxes(): void {
    this.redrawDifferenceAnnotations();
    this.redrawSelectedDifferenceOverlay();
  }

  /**
   * Hides difference boxes outside the current selection when Hide other differences is on,
   * and restores boxes this feature hid when the selection changes or the setting is turned off.
   */
  public static syncHiddenOtherDifferenceBoxes(): void {
    const state = store.getState();
    const hideOtherDifferences = getHideOtherDifferences(state);
    const focusedDifferenceId = getFocusedDifferenceId(state);
    const focusedGroupId =
      hideOtherDifferences && focusedDifferenceId && focusedDifferenceId !== '-1'
        ? getFocusedDifference(state)?.groupId || undefined
        : undefined;
    const displayedDifferenceIds = new Set(
      getDisplayedAndHiddenDifferences(state).displayed.map((difference) => difference.id),
    );

    [DocumentTypes.source, DocumentTypes.target].forEach((document) => {
      const annotManager = PDFAnnotationManager.getInstance(document);
      if (!annotManager) return;

      const annotations = annotManager.getAnnotationsList().map((annot) => ({
        id: annot.Id,
        groupId: annot.getCustomData('groupId') || undefined,
        hidden: annot.Hidden === true,
        hiddenByHideOthers: annot.getCustomData(AnnotationCustomData.hiddenByHideOtherDifferences) === 'true',
        hiddenByResync: annot.getCustomData('resyncHidden') === 'true',
        isDifference: annot.getCustomData(AnnotationCustomData.difference) === 'true',
        isDeletionMarker: annot.getCustomData(AnnotationCustomData.deletionMarker) === 'true',
      }));

      const { hideIds, showIds, clearFlagIds } = planDifferenceBoxVisibility({
        hideOtherDifferences,
        focusedDifferenceId,
        focusedGroupId,
        displayedDifferenceIds,
        annotations,
      });

      const hideAnnots = hideIds
        .map((id) => annotManager.getAnnotationById(id))
        .filter((annot): annot is Core.Annotations.Annotation => Boolean(annot));
      hideAnnots.forEach((annot) => annot.setCustomData(AnnotationCustomData.hiddenByHideOtherDifferences, 'true'));
      if (hideAnnots.length) annotManager.hideAnnotations(hideAnnots);

      clearFlagIds.forEach((id) => {
        annotManager.getAnnotationById(id)?.setCustomData(AnnotationCustomData.hiddenByHideOtherDifferences, 'false');
      });
      const showAnnots = showIds
        .map((id) => annotManager.getAnnotationById(id))
        .filter((annot): annot is Core.Annotations.Annotation => Boolean(annot));
      if (showAnnots.length) annotManager.showAnnotations(showAnnots);
    });
  }

  public static redrawSelectedDifferenceOverlay(): void {
    const state = store.getState();
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;
    const focusedDifference = getFocusedDifference(state);
    if (!focusedDifference) return;
    // Get the selected group
    const groupOrDifference = getGroupOrDifferenceId(state, focusedDifference.id);
    const differencesInGroup = getGroupedDifferences(groupOrDifference)(state);
    // Redraw overlays using applyHighContrast to respect source-insertion rules.
    // Scoped to each side's visible pages so a large group doesn't refresh every page it touches on
    // every toggle; off-screen pages are redrawn lazily as they scroll into view (their overlays'
    // captured visibility flags no longer match, so the lazy hook recreates them).
    if (differencesInGroup && differencesInGroup.length > 0) {
      const sourceVisiblePages =
        docManager.source?.docViewer.getDisplayModeManager().getDisplayMode().getVisiblePages() ?? [];
      const targetVisiblePages =
        docManager.target?.docViewer.getDisplayModeManager().getDisplayMode().getVisiblePages() ?? [];
      if (sourceVisiblePages.length) {
        docManager.applyHighContrast(differencesInGroup, {
          pages: sourceVisiblePages,
          documentType: DocumentTypes.source,
        });
      }
      if (targetVisiblePages.length) {
        docManager.applyHighContrast(differencesInGroup, {
          pages: targetVisiblePages,
          documentType: DocumentTypes.target,
        });
      }
    }
    // Reset flashing for graphic differences
    if (
      focusedDifference.type === DifferenceTypeNames.Graphics ||
      focusedDifference.type === DifferenceTypeNames.EmbeddedImage
    ) {
      docManager.GraphicDifferenceFocused([focusedDifference]);
    }
  }

  public static changeDocumentAnnotationVisibility(
    document: DocumentTypes,
    differences: Difference[],
    repeats: CtfMatchResultWithDifferences[] = [],
    display = true,
    draw = true,
  ) {
    const instance = PDFAnnotationManager.getInstance(document);

    const annotations: Core.Annotations.Annotation[] = [];
    const newAnnotations: Difference[] = [];
    let annotation: Core.Annotations.Annotation | undefined;
    let textHighlight: Core.Annotations.Annotation | undefined;
    const showTextHighlights = store.getState().app.showTextHighlights;

    // Debug: Check if excluded differences are being passed to this method
    const excludedDiffs = differences.filter((diff) => diff.excluded);
    if (excludedDiffs.length > 0) {
      console.log(`🚨 changeDocumentAnnotationVisibility (${document}) received EXCLUDED differences:`, {
        excludedCount: excludedDiffs.length,
        excludedIds: excludedDiffs.map((d) => d.id),
        display,
        draw,
      });
    }

    differences.forEach((diff) => {
      annotation = instance?.getAnnotationById(diff.id);
      if (annotation) {
        if (document === DocumentTypes.source) {
          const embeddedImageDeletionOnSource =
            DifferenceClass.getEmbeddedImageDeletionTarget(diff) === DocumentTypes.source;
          if (diff.type !== DifferenceTypeNames.Insertion && !embeddedImageDeletionOnSource) {
            annotations.push(annotation);
          }
        } else if (document === DocumentTypes.target && !DifferenceClass.requiresDeletionMarker([diff])) {
          annotations.push(annotation);
        }
      } else if (diff) {
        newAnnotations.push(diff);
      }

      // hide and show text highlights
      textHighlight = instance?.getAnnotationById(`${diff.id}-text-highlight`);

      if (textHighlight && showTextHighlights) {
        annotations.push(textHighlight);
      }
    });

    // handle repeat annotations for step and repeat inspections
    const repeatAnnotations: Core.Annotations.Annotation[] = [];
    const newRepeatAnnotations: CtfMatchResultWithDifferences[] = [];

    if (document === DocumentTypes.target) {
      repeats.forEach((repeat) => {
        annotation = instance?.getAnnotationById(`repeat-${repeat.index}`);
        if (annotation) {
          repeatAnnotations.push(annotation);
        } else {
          newRepeatAnnotations.push(repeat);
        }
      });
    }

    if (display) {
      // if the annotations are not already in the docs we need to draw them otherwise we just change visibility
      instance?.showAnnotations([...annotations, ...repeatAnnotations]);

      if (draw) {
        PDFAnnotationManager.drawAllTexts(newAnnotations, document);
        PDFAnnotationManager.drawRepeatAnnotations(newRepeatAnnotations);
      }
    } else {
      instance?.hideAnnotations([...annotations, ...repeatAnnotations]);
    }
  }

  public static changeDocumentsAnnotationVisibility(
    differences: Difference[],
    repeats: CtfMatchResultWithDifferences[] = [],
    display = true,
    draw = true,
  ): void {
    const isSingleFile = getIsSingleFile(store.getState());
    if (!isSingleFile) {
      this.changeDocumentAnnotationVisibility(DocumentTypes.source, differences, [], display, draw);
    }
    this.changeDocumentAnnotationVisibility(DocumentTypes.target, differences, repeats, display, draw);
    this.syncHiddenOtherDifferenceBoxes();
  }

  public static getSelectedId(): string {
    return this.selectedId;
  }

  public static bringInternalAnnotationsToFront(documentType: DocumentType, pageNumber?: number): void {
    const annotationManager = this.getInstance(documentType);
    if (!annotationManager) return;

    // Get all internal annotations
    const annotationsToFront = annotationManager.getAnnotationsList().filter((annot) => {
      return !annot.getCustomData('gvAnnotation') && (pageNumber === undefined || annot.PageNumber === pageNumber);
    });
    // Bring each annotation to front if it exists
    if (annotationsToFront.length > 0) {
      annotationsToFront.forEach((annot) => {
        if (annotationManager.getAnnotationById(annot.Id)) {
          annotationManager.bringToFront(annot);
        }
      });
    }
  }

  public static bringAnnotationsToFront(document: DocumentTypes, annotations: Core.Annotations.Annotation[]) {
    annotations.forEach((annot) => {
      PDFAnnotationManager.getInstance(document)?.bringToFront(annot);
    });
  }

  public static async drawRepeatAnnotations(repeats: CtfMatchResultWithDifferences[]): Promise<void> {
    const annotationManager = this.getInstance(DocumentTypes.target);
    const instance = PDFTronManager.getPDFInstance(DocumentTypes.target);

    if (!annotationManager || !instance) return;

    // Clean up old repeat annotations that are not in the current repeats list
    const allAnnotations = annotationManager.getAnnotationsList() || [];
    const existingRepeatAnnotations = allAnnotations.filter((annot) => annot.Id && annot.Id.startsWith('repeat-'));

    if (existingRepeatAnnotations.length > 0) {
      const currentRepeatIds = new Set(repeats.map((repeat) => `repeat-${repeat.index}`));

      const repeatAnnotationsToDelete = existingRepeatAnnotations.filter((annot) => !currentRepeatIds.has(annot.Id));

      if (repeatAnnotationsToDelete.length > 0) {
        annotationManager.deleteAnnotations(repeatAnnotationsToDelete, { imported: true, force: true });
      }
    }

    if (repeats.length === 0) return;

    repeats.forEach((repeat) => {
      const newRepeatAnnotation = new (GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation))();

      const hasDifferences = repeat.differences.length > 0;
      const annotationColor = hasDifferences ? '#FF9800' : '#2ED2AB';

      const { boundingBox } = repeat.matchDetails[0].sampleLocation;

      const x = boundingBox.left;
      const y = boundingBox.top;
      const width = boundingBox.right - boundingBox.left;
      const height = boundingBox.bottom - boundingBox.top;

      // Calculate label dimensions to expand annotation bounds
      const label = `Repeat ${repeat.index + 1}`;
      const iconSize = 12;
      const iconPadding = 2;
      const textPadding = 4;

      // We need to create a temporary canvas context to measure text
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;

      tempCtx.font = '11px Inter, sans-serif';
      const { width: textWidth } = tempCtx.measureText(label);
      const labelDimensions = { width: textWidth + textPadding * 2 + iconSize + iconPadding, height: 16 };

      // Get current page rotation to calculate initial bounds
      const pageNumber = repeat.matchDetails[0].sampleLocation.pageNumber;
      const currentRotation = getPageRotation(instance, pageNumber);
      const rectBounds = { x, y, width, height };

      // Bounds cover only the label so difference annotations inside the repeat remain clickable.
      // The draw method uses closure variables (x, y, width, height) for the visual rectangle.
      const annotationBounds = calculateLabelBounds(rectBounds, labelDimensions, currentRotation);

      newRepeatAnnotation.draw = (ctx: CanvasRenderingContext2D) => {
        // Get current page rotation dynamically at draw time so it updates when user rotates
        const pageRotation = instance ? getPageRotation(instance, repeat.matchDetails[0].sampleLocation.pageNumber) : 0;

        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = annotationColor;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x, y, width, height);

        // Calculate label anchor position based on page rotation to keep it in perceived top-left
        const bounds = {
          x: Math.min(x, x + width), // handle negative width
          y: Math.min(y, y + height), // handle negative height
          width: Math.abs(width),
          height: Math.abs(height),
        };
        const pgW = instance
          ? instance.Core.documentViewer.getPageWidth(repeat.matchDetails[0].sampleLocation.pageNumber)
          : undefined;
        const pgH = instance
          ? instance.Core.documentViewer.getPageHeight(repeat.matchDetails[0].sampleLocation.pageNumber)
          : undefined;
        const labelPosition = calculateRotatedLabelPosition(bounds, labelDimensions, pageRotation, 0, false, pgW, pgH);

        // Draw the label with counter-rotation so it's always horizontal and readable
        drawCounterRotatedLabel(
          ctx,
          label,
          labelPosition.x,
          labelPosition.y,
          labelDimensions.width,
          labelDimensions.height,
          pageRotation,
          annotationColor,
          textPadding,
          2, // borderRadius
          Utils.getRepeatIcon(),
          iconSize,
          iconPadding,
          '11px Inter, sans-serif',
        );
      };

      newRepeatAnnotation.Id = `repeat-${repeat.index}`;
      newRepeatAnnotation.IsHoverable = true;
      newRepeatAnnotation.NoResize = true;
      newRepeatAnnotation.ReadOnly = true;
      newRepeatAnnotation.PageNumber = pageNumber;
      newRepeatAnnotation.X = annotationBounds.x;
      newRepeatAnnotation.Y = annotationBounds.y;
      newRepeatAnnotation.Width = annotationBounds.width;
      newRepeatAnnotation.Height = annotationBounds.height;
      newRepeatAnnotation.setCustomData(RepeatAnnotationCustomData.repeatAnnotation, 'true');
      newRepeatAnnotation.setCustomData(AnnotationCustomData.matchIndex, repeat.index.toString());
      const rotationIndex = repeat.matchDetails[0].sampleLocation.rotationIndex;
      if (rotationIndex) {
        newRepeatAnnotation.setCustomData(GraphicZoneCustomData.rotationIndex, rotationIndex.toString());
      }

      // Store original bounds for high contrast overlay and zoom centering
      newRepeatAnnotation.setCustomData(
        'originalBounds',
        JSON.stringify({
          x: x,
          y: y,
          width: width,
          height: height,
        }),
      );

      // Store label dimensions for recalculating bounds on rotation change
      newRepeatAnnotation.setCustomData('labelDimensions', JSON.stringify(labelDimensions));

      annotationManager.addAnnotation(newRepeatAnnotation);
      annotationManager.redrawAnnotation(newRepeatAnnotation);
    });

    // Ensure difference annotations are above repeat annotations for click priority
    if (repeats.length > 0) {
      const allDifferenceAnnotations = annotationManager
        .getAnnotationsList()
        .filter((annot) => annot.getCustomData('difference') === 'true');
      if (allDifferenceAnnotations.length > 0) {
        PDFAnnotationManager.bringAnnotationsToFront(DocumentTypes.target, allDifferenceAnnotations);
      }
    }
  }

  /** Updates repeat annotation label-only bounds when page rotation changes. */
  public static updateRepeatAnnotationBoundsForRotation(pageNumber: number) {
    const annotationManager = this.getInstance(DocumentTypes.target);
    const instance = PDFTronManager.getPDFInstance(DocumentTypes.target);
    if (!annotationManager || !instance) return;

    const currentRotation = getPageRotation(instance, pageNumber);
    const repeatAnnotations = annotationManager
      .getAnnotationsList()
      .filter(
        (annot) =>
          annot.getCustomData(RepeatAnnotationCustomData.repeatAnnotation) === 'true' &&
          annot.PageNumber === pageNumber,
      );

    repeatAnnotations.forEach((annotation) => {
      const originalBoundsData = annotation.getCustomData('originalBounds');
      const labelDimensionsData = annotation.getCustomData('labelDimensions');
      if (!originalBoundsData || !labelDimensionsData) return;

      try {
        const rectBounds = JSON.parse(originalBoundsData);
        const labelDimensions = JSON.parse(labelDimensionsData);
        const newBounds = calculateLabelBounds(rectBounds, labelDimensions, currentRotation);

        annotation.X = newBounds.x;
        annotation.Y = newBounds.y;
        annotation.Width = newBounds.width;
        annotation.Height = newBounds.height;
        annotationManager.redrawAnnotation(annotation);
      } catch (err) {
        console.error('Failed to update repeat annotation bounds:', err);
      }
    });
  }

  public static repeatAnnotationSelected(annotation: Core.Annotations.Annotation) {
    const annotationManager = this.getInstance(DocumentTypes.target);
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (!annotationManager || !pdfDocManager) return;

    const viewerWrapper = pdfDocManager.getInstance(DocumentTypes.target);
    if (!viewerWrapper) return;

    if (getFocusedDifference(store.getState())) {
      store.dispatch(inspection.actions.unfocusDifference());
    }

    // Deselect all annotations on both viewers
    annotationManager.deselectAllAnnotations();
    this.getInstance(DocumentTypes.source)?.deselectAllAnnotations();

    // Reset source viewport to fit-to-page since no difference is focused
    const sourceWrapper = pdfDocManager.getInstance(DocumentTypes.source);
    const { zoomLockLevel } = useZoomStateStore.getState();
    if (sourceWrapper && !zoomLockLevel[DocumentTypes.source]) {
      sourceWrapper.fitToPage();
    }

    store.dispatch(app.actions.setFocusedMatchIndex(Number(annotation.getCustomData(AnnotationCustomData.matchIndex))));

    pdfDocManager.zoomToDifference(annotation.Id);
    pdfDocManager.scrollToDifferences(annotation.Id);
    useZoomStateStore.getState().setZoomChanging();

    viewerWrapper.removeHighContrasts();
    viewerWrapper.repeatAnnotationHighContrast([annotation]);
  }

  public static repeatAnnotationDeselected(annotation: Core.Annotations.Annotation) {
    const { zoomLockLevel, setZoomChanging } = useZoomStateStore.getState();
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;

    const viewerWrapper = docManager.getInstance(DocumentTypes.target);
    if (!viewerWrapper) return;

    viewerWrapper.removeHighContrasts();

    store.dispatch(app.actions.setFocusedMatchIndex(-1));

    if (!zoomLockLevel[DocumentTypes.target]) {
      viewerWrapper.fitToPage();
      viewerWrapper.setAnnotationPageNumber(annotation.Id);
      setZoomChanging();
    }
  }

  public static markupZoneAnnotationSelected(annotation: Core.Annotations.Annotation) {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (!pdfDocManager) return;

    // Saga will handle zoom and scroll after overlays are created
    store.dispatch(inspection.actions.focusDifference(annotation.Id));
  }

  // Timeout for text match highlight auto-deletion
  private static textMatchHighlightTimeout: NodeJS.Timeout | null = null;

  /**
   * Creates temporary highlight annotations for text match navigation on both documents.
   * Highlights the clicked word and the matching word, zooms/centers on the target, auto-removes after 3 seconds.
   */
  static createTempTextMatchHighlight(
    clicked: {
      documentType: DocumentTypes;
      location: { pageNumber: number; boundingBox: { left: number; right: number; top: number; bottom: number } };
    },
    target: {
      documentType: DocumentTypes;
      location: { pageNumber: number; boundingBox: { left: number; right: number; top: number; bottom: number } };
    },
    duration: number = 3000,
  ): void {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();

    // Clear any existing timeout
    if (PDFAnnotationManager.textMatchHighlightTimeout) {
      clearTimeout(PDFAnnotationManager.textMatchHighlightTimeout);
      PDFAnnotationManager.textMatchHighlightTimeout = null;
    }

    // Delete all previous text match highlights on both documents
    for (const docType of [DocumentTypes.source, DocumentTypes.target]) {
      const inst = PDFTronManager.getPDFInstance(docType);
      if (!inst) continue;
      const am = inst.Core.documentViewer.getAnnotationManager();
      const existing = am.getAnnotationsList().filter((annot) => annot.getCustomData('textMatchHighlight') === 'true');
      if (existing.length > 0) {
        am.deleteAnnotations(existing, { force: true, imported: true });
        inst.Core.documentViewer.refreshAll();
        inst.Core.documentViewer.updateView();
      }
    }

    // Helper to create a highlight on a specific document
    const addHighlight = (docType: DocumentTypes, loc: typeof clicked.location) => {
      const inst = PDFTronManager.getPDFInstance(docType);
      if (!inst) return;
      const { Core } = inst;
      const am = Core.documentViewer.getAnnotationManager();

      class TextMatchHighlight extends GVAnnotationMixin(Core.Annotations.RectangleAnnotation) {
        constructor() {
          super();
          this.setCustomData('textMatchHighlight', 'true');
          this.PageNumber = loc.pageNumber;
          this.X = loc.boundingBox.left;
          this.Y = loc.boundingBox.top;
          this.Width = loc.boundingBox.right - loc.boundingBox.left;
          this.Height = loc.boundingBox.bottom - loc.boundingBox.top;
          this.StrokeColor = new Core.Annotations.Color(0, 0, 0, 0);
          this.FillColor = new Core.Annotations.Color(0, 255, 0, 0.3);
          this.StrokeThickness = 0;
          this.NoMove = true;
          this.ReadOnly = true;
          this.Listable = false;
        }
      }

      const highlight = new TextMatchHighlight();
      am.addAnnotation(highlight);
      am.redrawAnnotation(highlight);
      return highlight;
    };

    // Create highlights on both documents
    addHighlight(clicked.documentType, clicked.location);
    const targetHighlight = addHighlight(target.documentType, target.location);

    // Zoom and center on the target match (respecting zoom lock)
    const { zoomLockLevel } = useZoomStateStore.getState();
    const targetWrapper = pdfDocManager?.[target.documentType];
    if (targetWrapper && targetHighlight) {
      const isZoomLocked = !!zoomLockLevel[target.documentType];

      // Always navigate to the correct page
      const targetDocViewer = targetWrapper.instance.Core.documentViewer;
      targetDocViewer.setCurrentPage(targetHighlight.PageNumber, true);

      // Only zoom if not locked
      if (!isZoomLocked) {
        try {
          const viewElement = targetDocViewer.getScrollViewElement();
          const padding = pdfDocManager.calculateDynamicPadding(
            targetHighlight,
            viewElement.clientWidth,
            viewElement.clientHeight,
            targetWrapper,
          );
          targetWrapper.setDifferenceZoomLevel(targetHighlight, false, {
            padding,
            upperBound: ZoomLevelLimits.differenceMax,
          });
        } catch {
          targetWrapper.setDifferenceZoomLevel(targetHighlight, false, {
            padding: 200,
            upperBound: ZoomLevelLimits.differenceMax,
          });
        }
        useZoomStateStore.getState().setZoomChanging();
      }

      setTimeout(() => targetWrapper.centerOnAnnotation(targetHighlight), 150);
    }

    // Auto-remove all highlights after duration
    PDFAnnotationManager.textMatchHighlightTimeout = setTimeout(() => {
      for (const docType of [DocumentTypes.source, DocumentTypes.target]) {
        const inst = PDFTronManager.getPDFInstance(docType);
        if (!inst) continue;
        const am = inst.Core.documentViewer.getAnnotationManager();
        const current = am.getAnnotationsList().filter((annot) => annot.getCustomData('textMatchHighlight') === 'true');
        if (current.length > 0) {
          am.deleteAnnotations(current, { force: true, imported: true });
          inst.Core.documentViewer.refreshAll();
          inst.Core.documentViewer.updateView();
        }
      }
      PDFAnnotationManager.textMatchHighlightTimeout = null;
    }, duration);
  }
}
